import { useCallback, useEffect, useRef, useState } from 'react';

import { FilterType } from '@/types/asset-search.types';
import Search from '@ingka/search';
import { translate } from '@repo/phrase';
import { useRegisterCommands } from '@sp-saljapro/command-menu/react';

import { useSearch } from '@/components/filters/search/search.hooks';
import { isValidAssetSearchFilter, isValidAssetSearchInput } from '@/components/filters/search/search.utils';

import { trackCommand } from '@/utils/command-menu.utils';

import styles from './styles.module.scss';

const SEARCH_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const;

const isEditableElement = (element: EventTarget | null) => {
    if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
    ) {
        return true;
    }

    return element instanceof HTMLElement && element.isContentEditable;
};

export const SearchAsset = () => {
    const { addFilter, removeFilter, assetIdFilter } = useSearch();
    const initialSearchQuery = assetIdFilter ?? '';

    return (
        <SearchAssetInput
            key={initialSearchQuery}
            addFilter={addFilter}
            removeFilter={removeFilter}
            assetIdFilter={assetIdFilter}
            initialSearchQuery={initialSearchQuery}
        />
    );
};

interface SearchAssetInputProps {
    addFilter: (filter: FilterType, value: string) => void;
    removeFilter: (filter: FilterType) => void;
    assetIdFilter: string | null;
    initialSearchQuery: string;
}

const SearchAssetInput = ({ addFilter, removeFilter, assetIdFilter, initialSearchQuery }: SearchAssetInputProps) => {
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isSearchQueryInvalid = searchQuery.length > 0 && !isValidAssetSearchInput(searchQuery);
    const canSubmitSearchQuery = searchQuery.length > 0 && isValidAssetSearchFilter(searchQuery);

    const appendToSearch = useCallback((value: string) => {
        searchInputRef.current?.focus();
        setSearchQuery((currentSearchQuery) => `${currentSearchQuery}${value}`);
    }, []);

    const onSearch = () => {
        // Prevents empty or invalid search query
        if (searchQuery.length === 0 || !isValidAssetSearchFilter(searchQuery)) {
            return;
        }
        addFilter(FilterType.AssetId, searchQuery);
    };

    const onClear = useCallback(() => {
        setSearchQuery('');
        removeFilter(FilterType.AssetId);
    }, [removeFilter, setSearchQuery]);

    useEffect(() => {
        const handlePaste = (event: ClipboardEvent) => {
            if (isEditableElement(event.target)) {
                return;
            }

            const clipboardText = event.clipboardData?.getData('text') ?? '';
            const pastedText = clipboardText.trim();

            if (pastedText.length === 0) {
                return;
            }

            event.preventDefault();
            appendToSearch(pastedText);
        };

        document.addEventListener('paste', handlePaste);

        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [appendToSearch]);

    useRegisterCommands({
        enabled: true,
        commands: SEARCH_DIGITS.map((digit) => ({
            id: `assets-list:typing-order-id-${digit}`,
            keys: [digit],
            hidden: true,
            onSelect: (source) => {
                trackCommand('assets-list:typing-order-id', source);
                appendToSearch(digit);
            },
        })),
    });
    const searchSubmitProps = canSubmitSearchQuery ? { onSearch } : {};

    return (
        <Search
            ref={searchInputRef}
            id="search-asset"
            data-testid="search-asset"
            className={String(isSearchQueryInvalid ? styles.invalidSearch : undefined)}
            clearBtnText={translate('asset_search_clear')}
            submitBtnText={translate('asset_search_submit')}
            ariaLabel={translate('asset_search_placeholder')}
            placeholder={translate('asset_search_placeholder')}
            value={searchQuery}
            aria-invalid={isSearchQueryInvalid}
            {...searchSubmitProps}
            onClear={onClear}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const nextSearchQuery = e.target.value;
                setSearchQuery(nextSearchQuery);

                if (nextSearchQuery.length === 0 && assetIdFilter) {
                    removeFilter(FilterType.AssetId);
                }
            }}
        />
    );
};
